// src/features/inventory/pages/ItemDetailPage/tabs/Suppliers/LinkSupplierDialog.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { suppliersAPI } from '../../../../services/suppliers';
import { useApi } from '../../../../../../hooks/api/useApi';

export default function LinkSupplierDialog({ open, onClose, onSuccess, itemId, linkedSupplierIds = [] }) {
  const { t } = useTranslation(['inventory', 'common']);
  const { loading: listLoading, execute: listExecute } = useApi();
  const { loading: submitLoading, error: submitError, execute: submitExecute, clearError } = useApi();

  const [allSuppliers, setAllSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierItemNumber, setSupplierItemNumber] = useState('');
  const [barcode, setBarcode] = useState('');

  useEffect(() => {
    if (!open) return;
    listExecute(() => suppliersAPI.getAll()).then(result => {
      if (result.success) setAllSuppliers(result.data.data || []);
    });
  }, [open, listExecute]);

  const availableSuppliers = allSuppliers.filter(
    s => s.active && !linkedSupplierIds.includes(s.supplier_id)
  );

  const handleClose = () => {
    setSelectedSupplier(null);
    setSupplierItemNumber('');
    setBarcode('');
    clearError();
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedSupplier) return;
    const result = await submitExecute(() =>
      suppliersAPI.linkItem(selectedSupplier.supplier_id, {
        item_id: itemId,
        supplier_item_number: supplierItemNumber.trim() || null,
        barcode: barcode.trim() || null,
      })
    );
    if (result.success) {
      onSuccess();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('inventory:items.tabs.suppliers.dialog.title')}</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {submitError && (
            <Alert severity="error" onClose={clearError}>{submitError}</Alert>
          )}

          {/* Supplier selector */}
          <Autocomplete
            options={availableSuppliers}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            value={selectedSupplier}
            onChange={(_, value) => setSelectedSupplier(value)}
            loading={listLoading}
            disabled={submitLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('inventory:items.tabs.suppliers.dialog.supplier')}
                required
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {listLoading && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={({ key, ...props }, option) => (
              <li key={key} {...props} key={option.supplier_id}>
                <Box>
                  <Typography variant="body2" fontWeight="600">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.code}</Typography>
                </Box>
              </li>
            )}
            noOptionsText={t('inventory:items.tabs.suppliers.dialog.no_suppliers')}
          />

          {/* Supplier's reference number for this item */}
          <TextField
            label={t('inventory:items.tabs.suppliers.supplier_sku')}
            value={supplierItemNumber}
            onChange={(e) => setSupplierItemNumber(e.target.value)}
            disabled={submitLoading}
            helperText={t('inventory:items.tabs.suppliers.dialog.supplier_sku_hint')}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />

          {/* Barcode */}
          <TextField
            label={t('inventory:items.tabs.suppliers.barcode')}
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            disabled={submitLoading}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitLoading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedSupplier || submitLoading}
          startIcon={submitLoading ? <CircularProgress size={16} /> : null}
        >
          {submitLoading ? t('common:buttons.saving') : t('inventory:items.tabs.suppliers.dialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
